import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { TID } from "../constants/testIds";
import {
  Bank,
  Calculator,
  Users,
  Scroll,
  SignOut,
  ShieldStar,
  GitPullRequest,
} from "@phosphor-icons/react";
import { Toaster } from "sonner";
import FloatingAnalysisStatus from "./FloatingAnalysisStatus";
import AiChatWidget from "./AiChatWidget";

const NavItem = ({ to, icon: Icon, label, testId, end, badge }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    className={({ isActive }) =>
      [
        "group flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors",
        "hover:bg-slate-100 hover:text-slate-900",
        isActive
          ? "bg-slate-900 text-white hover:bg-slate-900 hover:text-white"
          : "text-slate-600",
      ].join(" ")
    }
  >
    <Icon size={18} weight="duotone" />
    <span className="tracking-tight flex-1">{label}</span>
    {badge ? (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950 min-w-[18px] text-center">
        {badge}
      </span>
    ) : null}
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";

  // Poll the pending-proposals count every 20s so reviewers see a badge.
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/edit-proposals/count");
        if (alive) setPendingCount(data?.pending || 0);
      } catch {}
    };
    tick();
    const t = setInterval(tick, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Persist chat-open preference in localStorage
  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="h-screen flex bg-slate-50 overflow-hidden">
      {/* Left sidebar */}
      <aside
        data-testid={TID.sidebar}
        className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col h-screen"
      >
        <div className="px-6 py-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-slate-900 text-white grid place-items-center font-heading font-bold">
              C
            </div>
            <div>
              <div className="font-heading font-bold text-slate-900 leading-tight">Choice Capital</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Lender Intel</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <NavItem to="/" end icon={Bank} label="Lender Directory" testId={TID.navLenders} />
          <NavItem to="/pricing" icon={Calculator} label="Deal Pricing" testId={TID.navPricing} />
          <NavItem to="/proposals" icon={GitPullRequest} label="Pending edits" testId="nav-proposals" badge={pendingCount || null} />
          {isAdmin && (
            <>
              <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Admin
              </div>
              <NavItem to="/admin" icon={ShieldStar} label="Admin Panel" testId={TID.navAdmin} />
              <NavItem to="/admin/users" icon={Users} label="User Management" testId={TID.navUsers} />
              <NavItem to="/admin/audit" icon={Scroll} label="Audit Log" testId={TID.navAudit} />
            </>
          )}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div
            data-testid={TID.userBadge}
            className="flex items-center gap-3 px-2 py-2 rounded-md"
          >
            <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-700 grid place-items-center text-xs font-semibold">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
              <div className="text-[10px] text-slate-500 flex items-center gap-1">
                {(isAdmin || user?.role === "leader") && <ShieldStar size={11} weight="fill" className={user?.role === "leader" ? "text-amber-500" : "text-blue-600"} />}
                {user?.role}
              </div>
            </div>
          </div>
          <button
            data-testid={TID.navLogout}
            onClick={handleLogout}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded-md transition-colors"
          >
            <SignOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main (independently scrollable) */}
      <main className="flex-1 min-w-0 h-screen overflow-y-auto">
        <div className="page-enter p-8 sm:p-10 max-w-[1400px]">
          <Outlet />
        </div>
      </main>

      <FloatingAnalysisStatus />
      <AiChatWidget />
      <Toaster richColors position="top-right" />
    </div>
  );
}
